import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { AgentState } from '@app/core/interfaces/c5/acd/agent-state';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { PhoneNumberDirection } from '@app/core/interfaces/c5/phone-number-direction';
import { PhoneNumberType } from '@app/core/interfaces/c5/phone-number-type';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { ServiceUser } from '@app/core/interfaces/service-user';
import { UserLevel } from '@app/core/models/user-level';
import { EmbeddedViewResponse } from '@app/shared/acd-cc/models/embedded-view-response';
import { EnhancedQueue } from '@app/shared/acd-cc/models/enhanced-queue';
import { TelesWidget } from '@app/shared/acd-cc/models/teles-widget';
import { pcrIsAvailable } from '@app/shared/acd-cc/utils/pcr/pcr-is-available';
import { pcrIsEnabled } from '@app/shared/acd-cc/utils/pcr/pcr-is-enabled';
import { PcrState } from '@app/shared/acd-cc/utils/pcr/pcr-state';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { Utils } from '@app/shared/services/utils.service';
import { Identifier } from '@app/utils/common';
import { loggerFor } from '@app/utils/logger';
import { FeatureStatus } from '@business/feature/interfaces/feature-status';
import { forkJoin, Observable } from 'rxjs';
import { catchError, first, map, mergeMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AcdCallCenterUtilsService {
  private readonly cache = new Map<string, EmbeddedViewResponse>();
  private logger = loggerFor('AcdCallCenterUtilsService');

  constructor(
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private http: HttpClient,
    private phoneNumberPipe: PhoneNumberPipe,
    private utils: Utils
  ) {}

  cssClassForAgentState(agentState: AgentState, label?: string) {
    if (label != null) {
      switch (agentState) {
        case 'LOGGED_IN':
          return 'text-bg-success';
        case 'LOGGED_OUT':
          return 'text-bg-light';
        case 'WRAP_UP':
          return 'text-bg-danger';
        case void 0:
          return 'text-bg-light';
        default:
          return 'text-bg-warning';
      }
    } else {
      switch (agentState) {
        case 'LOGGED_IN':
          return 'btn-success';
        case 'LOGGED_OUT':
          return 'btn-light';
        case 'WRAP_UP':
          return 'btn-danger';
        case void 0:
          return '';
        default:
          return 'btn-warning';
      }
    }
  }

  enhanceQueue(queue: Queue, user: ServiceUser): EnhancedQueue {
    const externalPhoneNumbers: TypeaheadEntry<number>[] = [];

    for (const phoneNumber of queue.phoneNumbers) {
      if (phoneNumber.type !== PhoneNumberType.INTERNAL) {
        const label = this.phoneNumberPipe.transform(phoneNumber);

        externalPhoneNumbers.push({
          key: label,
          label,
          meta: {
            isOutgoing: phoneNumber.dir === PhoneNumberDirection.OUT,
          },
          value: phoneNumber.id,
        });
      }
    }

    return {
      ...queue,
      externalPhoneNumbers,
      voiceMailActive: user.features.some(
        (feature) => feature.featName === 'VOICE_MAIL' && feature.status === FeatureStatus.ACTIVE
      ),
    };
  }

  getPcrState(user: ServiceUser): Observable<PcrState> {
    const callRecPermanentFeatures$ = this.enterpriseAuthorizationService
      .getEnterpriseOption$(EnterpriseOptionType.CallRecPermanentFeatures)
      .pipe(first());

    const hasCallRecService$ = this.enterpriseAuthorizationService
      .hasEnterpriseOption$(EnterpriseOptionType.CallRecService)
      .pipe(first());

    return forkJoin([callRecPermanentFeatures$, hasCallRecService$]).pipe(
      map(([callRecPermanentFeatures, hasCallRecService]) => {
        return {
          available: hasCallRecService && pcrIsAvailable(callRecPermanentFeatures),
          enabled: hasCallRecService && pcrIsEnabled(callRecPermanentFeatures, user),
        };
      })
    );
  }

  openEmbeddedView(callCenterId: Identifier, widget: TelesWidget, entityId?: Identifier): Observable<void> {
    const viewName = this.getViewName(widget, entityId);

    this.logger.info(`Open embedded view ${widget} using view name (tab): ${viewName}`);

    let authToken$: Observable<EmbeddedViewResponse>;
    authToken$ = this.getAuthToken(callCenterId, widget, entityId);

    return authToken$.pipe(
      map((response) => {
        const url = this.constructEmbedUrl(response, callCenterId, widget, entityId);

        window.open(url, viewName);
      })
    );
  }

  private getAuthToken(
    callCenterId: Identifier,
    widget: TelesWidget,
    entityId?: Identifier
  ): Observable<EmbeddedViewResponse> {
    const cacheKey = `${callCenterId}-${widget}-${entityId}`;
    const cached = this.cache.get(cacheKey);

    let token$: Observable<EmbeddedViewResponse>;
    if (cached) {
      const url = this.constructEmbedUrl(cached, callCenterId, widget, entityId);
      token$ = this.http.head(url).pipe(
        map(() => cached),
        catchError(() => {
          this.logger.debug('Old auth token is invalid, requesting new one.');
          return this.fetchAuthToken(callCenterId, widget, entityId);
        })
      );
    } else {
      token$ = this.fetchAuthToken(callCenterId, widget, entityId);
    }

    return token$.pipe(
      tap((response) => {
        this.cache.set(cacheKey, response);
      })
    );
  }

  private constructEmbedUrl(
    cached: EmbeddedViewResponse,
    callCenterId: Identifier,
    widget: TelesWidget,
    entityId: Identifier
  ): string {
    let url = cached.url + `?CallCenterId=${callCenterId}&cmd=${widget}`;

    if (typeof entityId != null) {
      url = url + `&id=${entityId}`;
    }

    url = url + '&language=de' + `&authToken=${cached.authToken.token}`;

    return url;
  }

  private fetchAuthToken(
    callCenterId: Identifier,
    widget: TelesWidget,
    entityId?: Identifier
  ): Observable<EmbeddedViewResponse> {
    return this.getUrl(
      `/callCenter/${callCenterId}/embedUrl?widget=${widget}&entityId=${entityId || ''}`
    ).pipe(mergeMap((url) => this.http.get<EmbeddedViewResponse>(url)));
  }

  private getUrl(url: string) {
    return this.utils.apiUrl({
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
      [UserLevel.EndUser]: `/user${url}`,
    });
  }

  private getViewName(widget: TelesWidget, entityId: Identifier): string {
    let viewName = `portal.${widget}`;

    if (entityId != null) {
      viewName += `.${entityId}`;
    }

    return viewName;
  }
}
